// ITEM 12 — LALA ANSWERS HER INBOX. THE LAW.
//
// Incoming DMs to HER ACCOUNTS are evaluated. Safe classes (gratitude, love, greetings, simple site
// questions) get an immediate reply in her register, SENT. Everything else lands in HER QUEUE with a
// draft attached, for her one-tap send or her rewrite. **DISTRESS IS NEVER AUTO-REPLIED, and it is
// never DRAFTED either.** Every auto-reply is ledgered.
//
// Imported by BOTH the server (which decides and sends) and the dashboard (which renders her queue),
// and RUN by `scripts/lr15-inbox-answer.mjs`. It imports exactly one thing — the three crisis
// regexes `src/triage.js` already owns — because a second copy of a distress pattern is a second
// answer key to the one question on this site where being wrong is worst (R13/R31).
//
// ---------------------------------------------------------------------------------------------
// THE THREE SENTENCES THIS FILE EXISTS TO ENFORCE:
//
//  1. **NOTHING IN THE DETERMINISTIC PASS CAN FORCE A SAFE CLASS.** It can only ever push a message
//     towards her queue. That asymmetry is structural, not a habit: a classifier wrong in the SAFE
//     direction sends a machine's words to somebody in trouble under her name; a classifier wrong in
//     the QUEUE direction makes her read one more message. Those two mistakes are not the same size.
//
//  2. **THE FAIL DIRECTION IS THE OPPOSITE OF THE ROOM'S GATE, AND THAT IS DELIBERATE.** LR1's
//     community gate fails OPEN (a down engine publishes) because fail-closed would silently
//     shadow-hide the whole room and nobody would find out. Here a down engine means NO AUTO-REPLY,
//     which is not a silent failure at all: the message lands in her queue, which is exactly where it
//     would have gone anyway. Failing closed here costs nothing and failing open costs her name.
//
//  3. **A HELD MESSAGE IS NEVER ANSWERED AND NEVER DRAFTED** (LR1 ruling 1). A reply is a receipt
//     that somebody read it, which is precisely what shadow-hide withholds. It is already in her Held
//     queue by the room's own mechanism; putting it in a second queue would be a second answer to one
//     question.
// ---------------------------------------------------------------------------------------------

import { DISTRESS_RE, MONEY_RE, IDENTITY_RE } from "./triage.js";

// ---------------------------------------------------------------------------
// THE CLASSES. **A BICONDITIONAL, NOT A LIST WITH AN IMPLIED COMPLEMENT** (R28/R29's fix shape).
//
// `safe` is declared on every row rather than "safe means not in the queue list", so a class added
// to this roster and forgotten cannot silently inherit either answer — the fixture asserts the two
// derived rosters partition this one exactly, and `isSafeClass` answers FALSE for a key nobody
// registered, which is the direction that costs a read rather than a reputation.
// ---------------------------------------------------------------------------
// **`redirect` IS S5's SECOND COLUMN AND IT IS DECLARED ON EVERY ROW FOR THE SAME REASON `safe` IS.**
// Her (c) names ONE class that gets the Ask Lala redirect instead of an answer, and "redirect means
// not one of the others" would let a class added tomorrow inherit either answer in silence. So every
// row says which it is, with the reason it is not the other, and the fixture holds the two rosters
// against each other: nothing that gets NO words may be a redirect class, and nothing safe may be
// one either (a safe class is answered, and her (a) redirect fires on the SECOND one, not the first).
// **HER QC (2026-08-07) — `closer` IS A SOCIAL CLOSE, AND IT IS A FLAG ON THE ROSTER RATHER THAN A
// SECOND LIST.** Her report: members reply *"thank you"* to something she sent and Lala answers with
// a wordy invitation. Her ruling: *a thank-you, an emoji, a "love this", any social close needs at
// MOST a one-line warm acknowledgment or NO reply at all — never redirects, never invitations, never
// offers.* `site-question` is deliberately NOT one: a second real question is not a social close, and
// pointing it at the reading room is still the right answer.
export const ANSWER_CLASSES = [
  { key: "gratitude", label: "Thank you", safe: true, closer: true, redirect: false, note: "They are thanking her. A warm line back and nothing else." },
  { key: "love", label: "Love", safe: true, closer: true, redirect: false, note: "Affection, blessings, good wishes." },
  { key: "greeting", label: "Hello", safe: true, closer: true, redirect: false, note: "Hello, good morning, checking in. No question in it." },
  { key: "site-question", label: "About the site", safe: true, redirect: false, note: "A simple question about how the house works, answerable in a sentence." },
  // **HER (c), AND THE ONLY REDIRECT CLASS THERE IS.** *"Anything long, personal or advice-seeking
  // -> brief kind redirect to Ask Lala, never engaged in the inbox."* It still reaches her lane with
  // its draft; what changes is that the member is not left in silence while it waits.
  { key: "advice", label: "Wants a reading", safe: false, redirect: true, note: "Asking about their life, their chart, their cards, what to do. Hers." },
  // Business is NOT redirected, and the reason is that the Ask Lala room is not where a press
  // enquiry belongs — pointing a journalist at a reading room is a wrong answer rather than a brief one.
  { key: "business", label: "Business", safe: false, redirect: false, note: "Collaboration, press, sponsorship, wholesale, being featured." },
  // Money is NOT redirected: a member asking about a charge who is answered with "come to the Ask
  // Lala room" has been told to go somewhere that cannot help her, by the house, about her card.
  { key: "money", label: "Money", safe: false, redirect: false, note: "Anything touching charges, refunds, prices or a card." },
  { key: "distress", label: "Distress", safe: false, redirect: false, note: "Grief, fear, harm, crisis. Never answered by anything but her." },
  // Ambiguous is NOT redirected, and this is the load-bearing one: it is the class every tie-break
  // and every engine failure points at, so redirecting it would make the redirect the house's answer
  // to anything nothing could read — including a message that is really about money or a person in
  // trouble whose words the pre-screen did not catch.
  { key: "ambiguous", label: "Unclear", safe: false, redirect: false, note: "Anything the classifier was not sure about. The default." },
];
export const ANSWER_CLASS_KEYS = ANSWER_CLASSES.map((c) => c.key);
export const SAFE_CLASS_KEYS = ANSWER_CLASSES.filter((c) => c.safe).map((c) => c.key);
export const QUEUE_CLASS_KEYS = ANSWER_CLASSES.filter((c) => !c.safe).map((c) => c.key);
export const answerClassValid = (k) => ANSWER_CLASS_KEYS.includes(String(k || ""));
export const isSafeClass = (k) => SAFE_CLASS_KEYS.includes(String(k || ""));
export const answerClassLabel = (k) => ANSWER_CLASSES.find((c) => c.key === String(k || ""))?.label || "";
/** HER QC (2026-08-07): the classes that are a social CLOSE. Derived, never a second list (R31). */
export const CLOSER_CLASS_KEYS = ANSWER_CLASSES.filter((c) => c.closer).map((c) => c.key);
export const isCloserClass = (k) => CLOSER_CLASS_KEYS.includes(String(k || ""));

// THE CLASS EVERY TIE-BREAK POINTS AT. Named once so no branch below can type a different default.
export const FALLBACK_CLASS = "ambiguous";

// **THE ONE CLASS THAT GETS NO WORDS AT ALL, DECLARED BY NAME AND WITH ITS REASON.**
// Her order separates "never auto-replied" from "lands in her queue with a draft attached", and for
// distress those two are in tension: a draft is one tap from being sent, so a drafted reply to
// somebody in crisis is an auto-reply with an extra step in front of it. The conservative reading is
// that a machine writes NOTHING to a person in trouble, so this class reaches her with the member's
// own words and nothing else. It is also the cheapest branch on the path — no engine call is made.
export const NO_DRAFT_CLASSES = ["distress"];
export const mayDraft = (cls) => !NO_DRAFT_CLASSES.includes(String(cls || ""));

// ---------------------------------------------------------------------------
// **S5 — THE REDIRECT. THE ONE SENTENCE THIS INBOX SAYS INSTEAD OF HAVING A CONVERSATION.**
//
// Her order, in her own words: greeting or small talk gets *one short warm line; if they keep
// talking, the redirect: for readings and real talk, visit the Ask Lala room, that is where I sit
// with you*, and anything long, personal or advice-seeking gets *a brief kind redirect to Ask Lala,
// never engaged in the inbox.* **The chatty reply class is RETIRED**; the inbox is a doorway.
//
// **IT IS DECLARED HERE RATHER THAN WRITTEN INTO A PROMPT STRING, WHICH IS 14-B5's LESSON:** this is
// member-facing copy, so it lives where the rest of the house's copy lives and a gate can read it. A
// sentence inside `INBOX_SYSTEM_ADDENDUM` is a sentence no fixture can hold and no engine repeats the
// same way twice — and this one is said to every member who keeps talking, so it has to be the same
// sentence every time.
//
// **AND IT COSTS NOTHING, WHICH IS WHY IT CAN BE SENT RATHER THAN DRAFTED.** A canned line needs no
// engine call, and it is GATED AT DECLARATION TIME — the fixture asserts it passes
// `replyCopyViolations` and fits under the cap — rather than at run time, because there is no model
// output here to check. Nothing on this path puts a machine's improvisation in her mouth.
// **HER QC (2026-08-07) — THE OLD SENTENCE IS RETIRED AND IS REWRITTEN HERE RATHER THAN OVERRIDDEN
// BELOW.** It read *"For readings and real talk, come find me in the Ask Lala room. That is where I
// sit with you."* — S5's own comment above quotes that as her wording from THAT round, and she has
// now called it the cliché AI warmth the voice gate bans. **This is a superseded ruling, not a gate
// failure**: a canned line is checked at declaration time, so no gate was ever asked about it.
// **The whole self-narrated-availability family is banned in `server/lala-voice-bans.json` too**, so
// a model cannot reach it either — the constant and the ban move together or the ban is decoration.
//
// What is left is the fact and nothing about her availability: readings are done in the reading
// room. **THE WORDING IS PARKED FOR HER WORD (R125 — copy is hers; DECISIONS INBOX-1)**, and this is
// the plainest true sentence in the meantime. It reaches only `advice` now: a social close is never
// redirected at all.
export const INBOX_REDIRECT =
  "Readings are done in the Ask Lala room. Ask me there and I will read it properly.";

export const REDIRECT_CLASSES = ANSWER_CLASSES.filter((c) => c.redirect).map((c) => c.key);
export const isRedirectClass = (k) => REDIRECT_CLASSES.includes(String(k || ""));

// **THE THREE REFUSALS NO REDIRECT MAY OUTRANK, DECLARED RATHER THAN LEFT TO THE CALLER'S ORDER.**
// All three mean nothing may be said into this conversation AT ALL: it is not her inbox, she wrote
// it herself, or the room is holding it (LR1 ruling 1 — a redirect is a receipt exactly as a reply
// is, and shadow-hide withholds receipts). The pass returns before any of them can reach here today,
// which is precisely why the law is DRIVEN over them (R129/R135): a guard whose input the corpus
// cannot supply is a guard nothing holds.
export const NEVER_REDIRECT_REFUSALS = ["not-her-inbox", "from-an-owner", "held", "under-strike"];

// ---------------------------------------------------------------------------
// **S5b — THE SHUTDOWN. *FURTHER MESSAGES IN THAT THREAD GET SILENCE, NOT ENGAGEMENT.***
//
// Her ruling appends the escalation spine to the inbox order: repeated boundary-pushing walks the same
// ladder as the room, and the last rung is that **Lala stops responding.** The record is the room's own
// (`lr_strikes`, one table, migration 079 gave it the third door); what this is, is the reading of that
// record on this path.
//
// **ANY STRIKE ON FILE MEANS NO MACHINE WORDS, AND THAT IS THE CONSERVATIVE READING OF HER LADDER.**
// The rungs she names for the inbox are *the warm redirect, the firm line, and the SHUTDOWN*, and only
// the redirect and the shutdown are built — **the firm line is member-facing copy and copy is hers**
// (R125), so rather than inventing a sentence in her voice, a member with a strike on file gets NOTHING
// automatic and her message goes to HER. Nothing is taken from the member: she can still write, she is
// still read, and a human answers her instead of a machine. It errs towards a person, which is the one
// direction this file's own sentence 1 says a mistake may err in.
//
// **IT IS NOT A SECOND SILENCE.** Rungs 3+ already refuse the send route outright (`ctx.viewer.silenced`
// at `handleSendMessage`), so those messages never reach this pass at all. What this reaches is rungs
// 1-2 — a member who has been WARNED and is still writing — which is precisely the case her order
// describes and the one nothing was doing anything about.
// **IT IS A NUMBER OR IT IS NOTHING, WHICH IS R136/R137's COERCION LESSON ON A COUNT.**
// `Number(true)` is 1 and `Number("")` is 0, so a coercing test would read a boolean as *one strike*
// and an empty string as *none*. This value is never composed by a member — it comes from
// `strikeCount`, whose query is `count(*)::int`, so the cast is what guarantees a JS number and is
// asserted in the fixture beside this: **drop the `::int` and pg hands back a STRING, at which point a
// coercing test still works and a typed one goes silently blind** (R8-4's direction), so the type test
// and the cast are held together or neither means anything.
export const shutdownInInbox = (strikes) =>
  typeof strikes === "number" && Number.isFinite(strikes) && strikes > 0;

// **THE WHOLE REDIRECT DECISION, ONE PURE FUNCTION, ASKED ONLY WHEN SOMETHING WAS REFUSED.**
// `alreadyRedirected` is the *told ONCE* half of her ruling and is a fact about the CONVERSATION
// rather than about this message, so it is read from the store and passed in.
export function redirectDecision({ cls = "", refusals = [], alreadyRedirected = false } = {}) {
  const list = (Array.isArray(refusals) ? refusals : []).map((r) => String(r));
  // Nothing was refused, so the reply itself is going out; there is nothing to say a redirect
  // INSTEAD of. This keeps the function total rather than relying on the caller only asking sometimes.
  if (!list.length) return { redirect: false, why: "nothing-refused" };
  // **TOLD ONCE, PLAINLY, AND THEN NOT AGAIN.** Her ruling for the shutdown says once; the same
  // discipline governs the redirect, because a canned line repeated on every message is the way a
  // person works out they are talking to a machine (the cooldown's own reasoning).
  if (alreadyRedirected) return { redirect: false, why: "already-redirected" };
  // Distress gets no words at all, and a redirect is words. This is the same law `mayDraft` states,
  // asked here so the class cannot reach the send path by a second door.
  if (!mayDraft(cls)) return { redirect: false, why: "no-words-by-law" };
  const hard = list.find((r) => NEVER_REDIRECT_REFUSALS.includes(r));
  if (hard) return { redirect: false, why: hard };
  if (isRedirectClass(cls)) return { redirect: true, why: "not-answered-in-the-inbox" };
  // **HER QC (2026-08-07) — A SOCIAL CLOSE IS NEVER REDIRECTED, AND THIS ARM SUPERSEDES THE ONE
  // BELOW IT FOR THOSE THREE CLASSES.**
  //
  // Her report is this exact path: a member says *"thank you"* to something the owner sent her, the
  // cooldown refuses a second warm line, and the clause below sent the redirect INSTEAD — so a
  // thank-you was answered with an invitation to go somewhere else. Her ruling: *social closers get
  // the one-liner or silence, never redirects, never invitations, never offers.*
  //
  // **SILENCE IS HER OWN WORD AND IT IS THE RIGHT ANSWER HERE.** A second thank-you does not need
  // anything back; the warm line already went. It sits ABOVE the kept-talking clause on purpose,
  // because that clause is right for everything it still covers — `site-question` is a real question
  // and pointing it at the reading room is still the correct brief answer.
  if (isCloserClass(cls)) return { redirect: false, why: "social-closer" };
  // **HER (a), SECOND HALF: "if they keep talking, the redirect".** A safe class refused for exactly
  // one reason, and that reason is the cooldown, IS somebody who was answered warmly and wrote again.
  // Any OTHER refusal beside it means the house could not read this message confidently, and an
  // unread message is hers rather than the redirect's.
  if (isSafeClass(cls) && list.length === 1 && list[0] === "already-answered") {
    return { redirect: true, why: "kept-talking" };
  }
  return { redirect: false, why: "queued" };
}
export const mayRedirect = (input) => redirectDecision(input).redirect === true;

// ---------------------------------------------------------------------------
// THE DETERMINISTIC PRE-SCREEN. Machine-first, LR1's cost architecture.
//
// The three crisis regexes are IMPORTED. What is declared here is the one class the triage pass has
// no vocabulary for, because a support inbox and a practitioner's DMs are not the same inbox: people
// write to her to ask for things.
// ---------------------------------------------------------------------------
// **THE CARDS ARE CARDS HERE, AND THE WALKTHROUGH FOUND THIS LIVE.** `MONEY_RE` is A1's, written for
// a SUPPORT inbox where "card" means the one that gets charged, and its `card` alternative carries no
// trailing word boundary — so on a TAROT SITE the single most common sentence anybody writes to Lala,
// *"the cards keep showing the eight of cups"*, was classified MONEY. It still went to her queue, so
// nothing unsafe happened; her lane just labelled a tarot question "Money", and a plain thank-you
// that mentioned cards could never be answered.
//
// **THE FIX IS THE ONE THIS HOUSE ALREADY USES: STRIP THE CARD SENSE FIRST.** `tarotScopeViolations`
// in server/gates.js does exactly this ("strips the named cards first — the Moon, the Sun, the Star
// and the Tower are cards on this site before they are anything else"). Widening A1's regex instead
// would change what the SUPPORT inbox does with a real billing complaint, which is not this item's
// to move. So the money question alone is asked of a string with the tarot sense removed; every
// other question, and every other class, still reads the member's words whole.
export const TAROT_CARD_SENSE_RE = new RegExp([
  "\\b(?:tarot|oracle)\\s+cards?\\b",
  "\\b(?:the|my|those|these|your|her|his|their)\\s+cards\\b",
  "\\bcards?\\s+(?:say|says|said|show|showed|shows|keep|kept|came|come|mean|meant|tell|told)\\b",
  "\\b(?:pull(?:ed|ing)?|draw|drew|drawn|flip(?:ped)?|turn(?:ed)?)\\s+(?:a\\s+|the\\s+|my\\s+)?cards?\\b",
  "\\bcard\\s+(?:reading|spread|pull)\\b",
].join("|"), "i");

export const BUSINESS_RE = /\b(?:collab(?:o?rat(?:e|ion|ing))?|partnership|sponsor(?:ed|ship)?|wholesale|bulk order|press|interview|podcast|feature (?:you|your)|promote (?:you|your)|affiliate|brand deal|media kit|business (?:proposal|inquiry|enquiry)|work together|my (?:brand|shop|store|company)|advertis)/i;

// THE PRE-SCREEN. Returns a forced class, or null to let the engine decide.
// **NOTHING IN HERE RETURNS A SAFE CLASS.** That is sentence 1 above, made structural: read the
// return values and every one of them is in `QUEUE_CLASS_KEYS`.
export function preScreenInbox(text) {
  const s = String(text ?? "");
  if (!s.trim()) return { cls: FALLBACK_CLASS, why: "empty message", by: "rules" };
  if (DISTRESS_RE.test(s)) return { cls: "distress", why: "distress or safety language", by: "rules" };
  // Identity, data and legal talk is not its own class here — it is hers to answer, and "ambiguous"
  // is the class that means exactly that. Naming a fifth queue class for it would add a lane to her
  // seat that behaves identically to one that already exists.
  if (IDENTITY_RE.test(s)) return { cls: FALLBACK_CLASS, why: "identity, data, or legal language", by: "rules" };
  // The ONLY question asked of a stripped string, and it is stripped of ONE thing (see above).
  if (MONEY_RE.test(s.replace(TAROT_CARD_SENSE_RE, " "))) return { cls: "money", why: "money language", by: "rules" };
  if (BUSINESS_RE.test(s)) return { cls: "business", why: "business language", by: "rules" };
  return null;
}

// Fold the engine's verdict in, UNDER the pre-screen, with every tie-break pointing at her queue.
//
// A safe class requires ALL THREE: the engine said so, the engine said it was sure, and the
// pre-screen did not object. Any one missing and it is hers.
export function classifyInbox(text, verdict = null) {
  const forced = preScreenInbox(text);
  if (forced) return { ...forced, forced: true };

  const guess = String(verdict?.cls || verdict?.category || "");
  const confident = verdict?.confident === true;

  if (isSafeClass(guess) && confident) {
    return { cls: guess, why: String(verdict?.why || ""), by: "engine", forced: false };
  }
  if (isSafeClass(guess)) {
    return { cls: FALLBACK_CLASS, why: "read as simple but not confidently, so it comes to you", by: "engine-demoted", forced: false };
  }
  if (answerClassValid(guess)) {
    return { cls: guess, why: String(verdict?.why || ""), by: "engine", forced: false };
  }
  // No usable verdict at all (engine down, malformed answer, a class nobody registered). SHE READS
  // IT. `by` is "unavailable" and not "engine", which matters: `autoSendViolations` requires an
  // ENGINE classification, so an unusable answer can never reach the send path even if some future
  // edit widened the class check.
  return { cls: FALLBACK_CLASS, why: "no reading available, so it comes to you", by: "unavailable", forced: true };
}

// ---------------------------------------------------------------------------
// THE COPY LAW ON THE REPLY ITSELF.
//
// The voice and register gates already run on this reply — it carries her name, so it passes the
// same stack every Lala surface passes (the GATE LAW). What is checked HERE is the handful of things
// that are only wrong *because it is unprompted and automatic*: a promise she has not made, a claim
// about an account nobody touched, a timeline, and the machinery.
// ---------------------------------------------------------------------------
// **S5's *BRIEF* CLAUSE, AS A NUMBER.** Her order opens on the word BRIEF and the addendum has always
// said *one or two sentences*; 480 characters is four or five of them, so the cap was agreeing with a
// sentence it did not enforce. 240 is two long sentences and it is the number *brief* means here.
//
// **TIGHTENING IT IS THE SAFE DIRECTION AND THAT IS WHY IT MAY MOVE AT ALL:** `too-long` is a
// REFUSAL, so a reply over the cap is not truncated, it lands in her lane with its draft intact and
// she reads one more message. This file's own sentence 1 is that a classifier wrong towards her queue
// costs a read and one wrong towards the send path costs her name.
export const AUTO_REPLY_MAX_CHARS = 240;

// **S5's BAN, IN HER WORDS: the "ask me whatever is on your heart" class is BANNED in the inbox.**
// A conversational invitation in a DM is an offer to have the reading right here, which is exactly
// what the redirect exists to decline — and it is the shape her own capture caught, so her capture is
// the failing fixture her order asks for.
//
// **THE DIRECTION THIS ERRS IN IS THE OPPOSITE OF MOST GATES IN THIS HOUSE, DELIBERATELY.** A false
// positive sends the message to her queue and she answers it herself, which costs a read; a miss puts
// an invitation in her mouth on a surface she has ruled is a doorway. So the list is generous.
//
// **A QUESTION BACK IS NOT BANNED BY SHAPE, AND THAT IS MEASURED RATHER THAN SHY:** the one safe
// class this inbox exists for is a simple question about the house, and answering one can carry a
// question ("Did you mean the Tarot room?"), so a ban on question marks would refuse the class the
// feature is for. The addendum forbids the question back in the PROMPT; what is ENFORCED here is the
// invitation itself, which is a shape a reader can tell from an answer.
const INVITATION_RE = new RegExp([
  "\\bask me (?:anything|whatever|any\\b|about|what)",
  "\\b(?:whatever|what)(?:'s| is)? (?:on|in) your (?:heart|mind)\\b",
  "\\btell me (?:more|about|what|everything)\\b",
  "\\bfeel free to\\b",
  "\\bdon'?t hesitate\\b",
  "\\breach out\\b",
  "\\blet me know\\b",
  "\\bI(?:'m| am) (?:here for you|all ears|listening)\\b",
  "\\bhow can I help\\b",
  "\\bwhat (?:can|may) I (?:do for|help)\\b",
  "\\bany ?time you (?:want|need)\\b",
  "\\bkeep (?:me posted|talking)\\b",
  "\\bwhat(?:'s| is) (?:going on|happening) with\\b",
  "\\bcome (?:tell|talk to) me\\b",
].join("|"), "i");

const MACHINERY_RE = /\b(?:automat(?:ed|ic|ically)|auto[\s-]?repl(?:y|ied)|bot\b|AI\b|A\.I\.|artificial intelligence|language model|classifier|algorithm|queue|ticket|triage|system generated|this is a generated)/i;
const TIMELINE_RE = /\b(?:within \d+\s*(?:hours?|days?|weeks?)|in \d+\s*(?:hours?|days?|weeks?)|business days?|by (?:tomorrow|monday|tuesday|wednesday|thursday|friday|the end of)|shortly|asap)\b/i;
const PROMISE_RE = /\b(?:I(?:'| wi)ll (?:add|build|make|fix|send|refund|upgrade|change|update|create|set up|look into|check on)|I'?m going to (?:add|build|fix|refund|upgrade)|let me (?:add|build|fix|refund|upgrade))\b/i;
const ACCOUNT_CLAIM_RE = /\b(?:I(?:'ve| have) (?:refunded|upgraded|cancelled|canceled|reset|deleted|fixed|added)|your (?:account|membership) (?:has been|is now)|I(?:'ve| have) (?:just )?(?:sorted|handled) (?:that|it))\b/i;

export function replyCopyViolations(text) {
  const s = String(text ?? "");
  const out = [];
  if (INVITATION_RE.test(s)) out.push("invites them to keep talking");
  if (MACHINERY_RE.test(s)) out.push("names the machinery");
  if (TIMELINE_RE.test(s)) out.push("promises a timeline");
  if (PROMISE_RE.test(s)) out.push("makes a promise on her behalf");
  if (ACCOUNT_CLAIM_RE.test(s)) out.push("claims something was done to their account");
  if (/—/.test(s)) out.push("uses an em dash");
  return out;
}

// ---------------------------------------------------------------------------
// **THE SEND GATE. ONE FUNCTION, AND IT IS THE WHOLE DECISION.**
//
// Everything that could stop an auto-reply is asked here, in one place, and the answer is a LIST of
// reasons rather than a boolean — because the reason is what her queue shows her and what the ledger
// records, and a boolean would make every refusal look the same. An empty list is the ONLY thing
// that sends.
//
// Written as a pure function of already-known facts so the fixture can drive every combination of
// them without a database, a browser or a vendor.
// ---------------------------------------------------------------------------
export const SEND_REFUSALS = [
  "not-her-inbox",        // nobody in this conversation is one of her accounts
  "from-an-owner",        // she wrote it; the house does not answer itself
  "held",                 // LR1 ruling 1
  "under-strike",         // S5b's SHUTDOWN: silence, not engagement
  "already-answered",     // the cooldown below
  "not-a-safe-class",
  "not-engine-classified",// the rules pass can only ever demote, so it can never send
  "no-engine-verdict",    // sentence 2: fail CLOSED here
  "no-reply-drafted",
  "too-long",
];

// **THE COOLDOWN, AND IT IS A JUDGEMENT CALL LOGGED IN DECISIONS.** Her order does not name one.
// Repeated machine replies in one conversation are how a person works out they are talking to a
// machine, and the second one adds nothing the first did not say. So one auto-reply per conversation
// per six hours; a second safe message inside that window lands in her queue with its draft instead
// of being answered. Nothing is lost — it is still answered, by her.
export const AUTO_REPLY_COOLDOWN_MS = 6 * 60 * 60 * 1000;

export function withinCooldown(lastSentAt, now) {
  if (!lastSentAt) return false;
  // `Date.parse` on a date-only string has no milliseconds in it and Postgres hands `timestamptz`
  // back as a Date, so both go through the constructor. `new Date(null)` is the EPOCH rather than
  // NaN, which is why null is guarded above rather than being left to arithmetic (ITEM 10's lesson).
  const then = new Date(lastSentAt).getTime();
  const at = now == null ? NaN : new Date(now).getTime();
  if (!Number.isFinite(then) || !Number.isFinite(at)) return false;
  return at - then < AUTO_REPLY_COOLDOWN_MS;
}

export function autoSendViolations({
  cls = "",
  by = "",
  held = false,
  recipientIsOwner = false,
  senderIsOwner = false,
  engineOk = false,
  reply = "",
  lastSentAt = null,
  now = null,
} = {}) {
  const out = [];
  if (!recipientIsOwner) out.push("not-her-inbox");
  if (senderIsOwner) out.push("from-an-owner");
  if (held) out.push("held");
  if (withinCooldown(lastSentAt, now)) out.push("already-answered");
  if (!isSafeClass(cls)) out.push("not-a-safe-class");
  if (String(by) !== "engine") out.push("not-engine-classified");
  if (!engineOk) out.push("no-engine-verdict");
  const text = String(reply || "").trim();
  if (!text) out.push("no-reply-drafted");
  else if (text.length > AUTO_REPLY_MAX_CHARS) out.push("too-long");
  out.push(...replyCopyViolations(text));
  return out;
}

export const maySend = (input) => autoSendViolations(input).length === 0;

// ---------------------------------------------------------------------------
// HER SEAT. The lane, its actions, and the sentences the SERVER composes for both (the D2 law: a
// confirm written on the client can describe something other than what the server then does).
// ---------------------------------------------------------------------------
// The tab itself is declared in `src/livingRoom.js`'s `QUEUE_TABS` — that file owns her seat's
// roster and imports nothing, so the key travels OUT of it rather than this copy travelling in.
export const EMPTY_INBOX_LANE = "Nothing waiting. Anything simple was already answered.";
export const SENT_MARK = "Answered for you";
export const NO_DRAFT_NOTE = "No draft was written for this one. These are yours to answer in your own words.";

export const INBOX_DRAFT_ACTIONS = [
  { key: "send", label: "Send", note: "Sends this into the conversation from your account." },
  { key: "dismiss", label: "Dismiss", note: "Clears it from here. Nothing is sent and the conversation is untouched." },
];
export const INBOX_DRAFT_ACTION_KEYS = INBOX_DRAFT_ACTIONS.map((a) => a.key);
export const inboxDraftActionValid = (k) => INBOX_DRAFT_ACTION_KEYS.includes(String(k || ""));

export function inboxDraftSentence({ action, memberName = "them" } = {}) {
  const who = String(memberName || "them").trim() || "them";
  if (action === "send") return `Send this to ${who}. It goes into the conversation from your account, now.`;
  if (action === "dismiss") return `Clear this from your DMs lane. Nothing is sent to ${who}.`;
  return "";
}

// The one-line ledger summary. Composed here so the service log reads the same whoever wrote the row
// — and so a new action cannot land in the ledger describing itself as `undefined`.
export function inboxLedgerSummary({ action, cls = "", refusals = [] } = {}) {
  const label = answerClassLabel(cls) || cls || "unclear";
  if (action === "sent") return `Answered a "${label}" message in your voice and sent it.`;
  // S5's redirect is its own ledger line rather than a "sent" one, because what went out was the
  // house's declared sentence and not words anything wrote for her — and her ledger is the one place
  // that difference is visible after the fact.
  if (action === "redirected") return `Pointed a "${label}" message at the Ask Lala room. Nothing else was said.`;
  if (action === "drafted") return `Drafted a reply to a "${label}" message and left it for you.`;
  if (action === "queued") {
    const why = refusals.length ? ` (${refusals.join(", ")})` : "";
    return `A "${label}" message came to you and was not answered${why}.`;
  }
  if (action === "owner-sent") return "You sent the drafted reply.";
  if (action === "owner-dismissed") return "You cleared the drafted reply. Nothing was sent.";
  return `Inbox: ${action}.`;
}

// What the DASHBOARD shows as the reason a message was not answered. The refusal keys above are
// machine words; these are hers. A key with no sentence falls back to the key rather than to
// silence, so a refusal added and forgotten is VISIBLE in her seat instead of reading as no reason.
export const REFUSAL_SENTENCES = {
  "not-her-inbox": "Not written to you.",
  "from-an-owner": "You wrote it.",
  held: "Held by the room's gate.",
  "already-answered": "Already answered recently in this conversation.",
  "not-a-safe-class": "Not a simple one.",
  "not-engine-classified": "Nothing read it confidently.",
  "no-engine-verdict": "Nothing could read it.",
  "no-reply-drafted": "No words came back.",
  "too-long": "The draft ran long.",
  // S5b — hers to answer, in her own words, because the ladder has already spoken to this member.
  "under-strike": "This member has a strike on file, so nothing was said automatically.",
};
export const refusalSentence = (key) => REFUSAL_SENTENCES[String(key || "")] || String(key || "");

// ---------------------------------------------------------------------------
// **INBOX-SILENCE (her order, 2026-08-08, launch day) — LALA DOES NOT ANSWER THE INBOX. AT ALL.**
//
// A member asked where support was and the pass offered them a READING in the Ask Lala room: the
// wrong answer in the wrong lane, to somebody who had asked for help. Her ruling, effective
// immediately: **Lala stops answering the inbox entirely.** This SUPERSEDES the triage-only ruling
// (I4) and every class-by-class rule under it — there is no longer a class that earns a reply.
//
// **WHAT STOPS IS DELIVERY, AND ONLY DELIVERY.** Her sentence keeps the rest: every inbox message
// still flows to the owner's queue, the classifier may still read it silently for her dashboard, and
// every row, draft, refusal and ledger line is written exactly as before — so her seat loses nothing
// and she can still see what a member wrote and what was drafted for it. What no member gets is a
// message back.
//
// **IT IS ASKED AT THE SENDER, NOT AT THE BRANCHES**, and that is L3's lesson rather than a
// preference: `answerInboxMessage` has TWO delivery sites (the redirect and the answer), and a guard
// written at one of them leaves the other live. `send` is the one thing both pass through, so
// withholding it is a property rather than something two branches have to remember. The function
// already treats an absent sender as a first-class state (`send ? await send(...) : null`), so every
// row below still records `sentMessageId: null` and nothing else in that pass changes shape.
//
// **ASK LALA THE ROOM IS UNTOUCHED** — her sentence, and this law is reachable only from the inbox
// pass. Nothing in `handleLalaChat` reads it.
//
// The env var is an escape hatch for HER, not a default: silence is what ships, and it takes a
// deliberate `LALA_INBOX_AUTOREPLY=on` to speak again.
// ---------------------------------------------------------------------------
export const INBOX_AUTOREPLY_DEFAULT = false;

/**
 * May the inbox pass deliver a message to a member? `env` is a PARAMETER so a gate RUNS this rather
 * than reading a line, and so a fixture can drive both answers without touching the process.
 *
 * **ONLY THE EXACT STRING `"on"` SPEAKS.** `Boolean("false")` is `true` and `Boolean("0")` is `true`
 * (R136/R137), so anything looser here turns a typo in Railway's config into Lala talking to members
 * again — which is the one outcome this law exists to prevent.
 */
export function inboxAutoReplyOn(env = {}) {
  return String(env.LALA_INBOX_AUTOREPLY || "").trim().toLowerCase() === "on";
}

// Why a message was not answered, for her dashboard, in her own words.
export const INBOX_SILENCED_REASON = "owner-silenced";
